import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User } from '../services/authService';
import api from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (token: string, userData: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isCheckingAuthRef = useRef(false);

  const checkAuth = React.useCallback(async () => {
    // Evitar múltiplas verificações simultâneas usando ref
    if (isCheckingAuthRef.current) {
      return;
    }
    isCheckingAuthRef.current = true;
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        // Definir usuário imediatamente para evitar delay
        // Usar função de atualização para evitar dependências desnecessárias
        setUser((prevUser) => {
          // Só atualizar se o usuário mudou
          if (!prevUser || prevUser.id !== parsedUser.id) {
            return parsedUser;
          }
          return prevUser;
        });
        setLoading(false);
        
        // Verificar se o token ainda é válido em background (sem bloquear)
        // Usar função de atualização para evitar re-renderizações desnecessárias
        try {
          const response = await api.get('/auth/me');
          setUser((prevUser) => {
            // Só atualizar se os dados mudaram
            const newUser = response.data.user;
            if (!prevUser || prevUser.id !== newUser.id || prevUser.email !== newUser.email) {
              localStorage.setItem('user', JSON.stringify(newUser));
              return newUser;
            }
            return prevUser;
          });
        } catch (error: any) {
          // Se o token for inválido (401), limpar e deslogar
          // Mas não deslogar por outros erros (rede, etc)
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
          // Para outros erros, manter o usuário logado
        }
      } catch (error) {
        console.error('Erro ao processar dados do usuário:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setLoading(false);
      }
    } else {
      setUser((prevUser) => {
        // Só atualizar se havia um usuário antes
        if (prevUser) {
          return null;
        }
        return prevUser;
      });
      setLoading(false);
    }
    isCheckingAuthRef.current = false;
  }, []);

  useEffect(() => {
    // Verificar autenticação ao montar o componente
    checkAuth();

    // Listener para mudanças no localStorage (quando login é feito em outra aba/janela)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Verificar periodicamente se o token ainda é válido (a cada 5 minutos)
    // Isso ajuda a manter a sessão ativa
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      // Verificar se há token e dados do usuário, sem depender do estado
      if (token && userData) {
        // Verificar token em background sem bloquear
        api.get('/auth/me')
          .then((response) => {
            // Usar função de atualização para evitar re-renderizações desnecessárias
            setUser((prevUser) => {
              const newUser = response.data.user;
              if (!prevUser || prevUser.id !== newUser.id || prevUser.email !== newUser.email) {
                localStorage.setItem('user', JSON.stringify(newUser));
                return newUser;
              }
              return prevUser;
            });
          })
          .catch((error: any) => {
            // Apenas deslogar se for erro 401 (não autorizado)
            if (error.response?.status === 401) {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setUser(null);
            }
          });
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [checkAuth]); // Removido 'user' das dependências para evitar loop infinito

  const login = (token: string, userData: User) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await api.get('/auth/me');
      const newUser = response.data.user;
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);
    } catch {
      // ignore
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
